import {
  Appointment,
  AppointmentContextBuilder,
  Patient,
  ScopeDataBuilder,
} from '@principle-theorem/principle-core';
import {
  AppointmentStatus,
  type IAppointment,
  type IPatient,
  type IPrincipleMention,
  type IResolvedTemplateContext,
  type ITemplateContextOption,
  MentionResourceType,
  PatientRelationshipType,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import {
  asDocRef,
  asyncForEach,
  DATE_TIME_FORMAT,
  getDoc,
  type IProvider,
  multiFilter,
  query$,
  snapshot,
  toMoment,
  where,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, first, memoize, orderBy, uniqBy } from 'lodash';
import * as moment from 'moment-timezone';
import { map } from 'rxjs/operators';

export class AppointmentMentionContextProvider
  implements IProvider<TemplateScope[], Promise<ITemplateContextOption[]>>
{
  constructor(
    private _mention: IPrincipleMention,
    private _appUrl: string
  ) {}

  canProvide(scopes: TemplateScope[]): boolean {
    const type = this._mention.resource.type;
    const isAppointmentScope = scopes.includes(TemplateScope.Appointment);
    const isAppointmentResource = type === MentionResourceType.Appointment;
    const isPatientResource = type === MentionResourceType.Patient;
    return isAppointmentScope && (isAppointmentResource || isPatientResource);
  }

  async execute(): Promise<ITemplateContextOption[]> {
    const type = this._mention.resource.type;
    const isPatientResource = type === MentionResourceType.Patient;

    if (isPatientResource) {
      return this._resolveAppointmentContextsFromPatient();
    }
    const appointment = await getDoc(
      asDocRef<IAppointment>(this._mention.resource.ref)
    );

    return [await this._resolveAppointmentContext(appointment)];
  }

  private async _resolveAppointmentContextsFromPatient(): Promise<
    ITemplateContextOption[]
  > {
    const patient = await getDoc(
      asDocRef<IPatient>(this._mention.resource.ref)
    );

    const futureAppointments = await snapshot(
      Patient.withPatientRelationships$(
        patient,
        [PatientRelationshipType.DuplicatePatient],
        (patientReffable) => Patient.getFutureAppointments$(patientReffable)
      )
    );

    const lastAppointment = await snapshot(
      Patient.withPatientRelationships$(
        patient,
        [PatientRelationshipType.DuplicatePatient],
        (patientReffable) =>
          Patient.lastAppointment$(patientReffable, moment()).pipe(
            map((appointment) => compact([appointment]))
          )
      ).pipe(
        map((appointments) =>
          first(orderBy(appointments, 'event.from', 'desc'))
        )
      )
    );

    const unscheduledAppointments = await snapshot(
      Patient.withPatientRelationships$(
        patient,
        [PatientRelationshipType.DuplicatePatient],
        (patientReffable) =>
          query$(
            Patient.appointmentCol(patientReffable),
            where('status', '==', AppointmentStatus.Unscheduled)
          ).pipe(multiFilter((appointment) => !appointment.deleted))
      )
    );

    const appointments = uniqBy(
      compact([
        lastAppointment,
        ...futureAppointments,
        ...unscheduledAppointments,
      ]),
      (appointment) => appointment?.ref.id
    );

    return asyncForEach(appointments, (appointment) =>
      this._resolveAppointmentContext(appointment)
    );
  }

  private async _resolveAppointmentContext(
    appointment: WithRef<IAppointment>
  ): Promise<ITemplateContextOption> {
    const practice = await snapshot(Appointment.practice$(appointment));

    if (!practice) {
      throw new Error('No practice found for appointment');
    }

    const startTime = appointment.event
      ? toMoment(appointment.event.from)
          .tz(practice.settings.timezone)
          .format(DATE_TIME_FORMAT)
      : 'Unscheduled';
    const label = `${startTime} - ${appointment.treatmentPlan.treatmentStep.name}`;

    return {
      label,
      scope: TemplateScope.Appointment,
      resolve: memoize(() => this._buildAppointmentContext(appointment)),
    };
  }

  private async _buildAppointmentContext(
    appointment: WithRef<IAppointment>
  ): Promise<IResolvedTemplateContext> {
    const scopeData = await ScopeDataBuilder.buildAppointmentScopeData(
      appointment,
      this._appUrl
    );
    const context = new AppointmentContextBuilder(scopeData).build();
    return { context, scopeData };
  }
}
